const ENGLISH_LOCALES = new Set(['en', 'en-us', 'en-gb', 'en_us', 'en_gb']);

/** Social copy is always sen.json — not a translatable foreign locale. */
export const SEN_JSON = 'sen.json';
export const SEN_FOLDER = 'sen';

const GENERIC_JSON_NAMES = new Set([
  'strings',
  'translations',
  'locale',
  'messages',
  'default'
]);

function normalizeLocaleCode(code) {
  return String(code || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

/** Strip leading/trailing slashes for consistent path comparison. */
function normalizeStoragePath(path) {
  return String(path || '')
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/\\/g, '/');
}

function pathBasename(path) {
  return String(path || '')
    .replace(/\\/g, '/')
    .replace(/\/+$/, '')
    .split('/')
    .pop()
    .toLowerCase();
}

/**
 * @param {string} path
 * @returns {boolean}
 */
export function isSenJsonPath(path) {
  return pathBasename(path) === SEN_JSON;
}

/**
 * Generate-from-English option for social copy (always writes sen.json).
 */
export const SEN_GENERATE_OPTION = {
  value: SEN_JSON,
  text: 'Social English (sen.json)'
};

/**
 * Infer locale code from a translation file path (e.g. /bg/bg.json → bg).
 * @param {string} path
 * @returns {string|null}
 */
export function localeFromPath(path) {
  if (!path || !/\.json$/i.test(path)) return null;
  if (isSenJsonPath(path)) return null;

  const parts = path.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  if (fileBase && !GENERIC_JSON_NAMES.has(fileBase.toLowerCase()) && fileBase.length <= 8) {
    return normalizeLocaleCode(fileBase);
  }

  if (parts.length >= 2) {
    const parent = parts[parts.length - 2];
    if (
      parent &&
      parent.length <= 8 &&
      !GENERIC_JSON_NAMES.has(parent.toLowerCase())
    ) {
      return normalizeLocaleCode(parent);
    }
  }

  if (GENERIC_JSON_NAMES.has(fileBase.toLowerCase())) return null;

  return fileBase ? normalizeLocaleCode(fileBase) : null;
}

export function isEnglishLocale(code) {
  if (!code) return false;
  const normalized = normalizeLocaleCode(code);
  return ENGLISH_LOCALES.has(normalized) || normalized.startsWith('en-');
}

export function isTranslatableLocale(code) {
  if (!code) return false;
  return normalizeLocaleCode(code) !== 'sen';
}

/**
 * Target languages for generate-from-English.
 * `value` matches translation repo filenames (e.g. cz.json, pt-br.json).
 */
export const COMMON_TARGET_LOCALES = [
  { value: 'bg', text: 'Bulgarian (bg)' },
  { value: 'cz', text: 'Czech (cz)' },
  { value: 'da', text: 'Danish (da)' },
  { value: 'de', text: 'German (de)' },
  { value: 'el', text: 'Greek (el)' },
  { value: 'es', text: 'Spanish (es)' },
  { value: 'et', text: 'Estonian (et)' },
  { value: 'fi', text: 'Finnish (fi)' },
  { value: 'fr', text: 'French (fr)' },
  { value: 'hr', text: 'Croatian (hr)' },
  { value: 'hu', text: 'Hungarian (hu)' },
  { value: 'id', text: 'Indonesian (id)' },
  { value: 'it', text: 'Italian (it)' },
  { value: 'ja', text: 'Japanese (ja)' },
  { value: 'ko', text: 'Korean (ko)' },
  { value: 'lv', text: 'Latvian (lv)' },
  { value: 'nl', text: 'Dutch (nl)' },
  { value: 'no', text: 'Norwegian (no)' },
  { value: 'pt', text: 'Portuguese (pt)' },
  { value: 'pt-br', text: 'Portuguese — Brazil (pt-br)' },
  { value: 'ro', text: 'Romanian (ro)' },
  { value: 'ru', text: 'Russian (ru)' },
  { value: 'sk', text: 'Slovak (sk)' },
  { value: 'sv', text: 'Swedish (sv)' },
  { value: 'th', text: 'Thai (th)' },
  { value: 'tr', text: 'Turkish (tr)' },
  { value: 'uk', text: 'Ukrainian (uk)' },
  { value: 'vi', text: 'Vietnamese (vi)' },
  { value: 'zh', text: 'Chinese Simplified (zh)' },
  { value: 'zh-ts', text: 'Chinese Simplified (zh-ts)' }
].filter(item => !isEnglishLocale(item.value) && isTranslatableLocale(item.value));

function localeFileName(code) {
  const normalized = String(code || '').trim().replace(/_/g, '-').toLowerCase();
  if (!normalized) return null;
  return `${normalized}.json`;
}

/**
 * Resolve sen.json path from an English source path (game/en/en.json → game/sen/sen.json).
 * @param {string} sourcePath
 * @returns {string|null}
 */
export function senPathFromEnglishPath(sourcePath) {
  if (!sourcePath) return null;

  const parts = sourcePath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const sourceLocale = localeFromPath(sourcePath);
  if (!sourceLocale || !isEnglishLocale(sourceLocale)) return null;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  const parent = parts.length >= 2 ? parts[parts.length - 2] : null;

  if (
    parent &&
    normalizeLocaleCode(parent) === sourceLocale &&
    normalizeLocaleCode(fileBase) === sourceLocale
  ) {
    const next = [...parts];
    next[next.length - 2] = SEN_FOLDER;
    next[next.length - 1] = SEN_JSON;
    return next.join('/');
  }

  if (normalizeLocaleCode(fileBase) === sourceLocale) {
    const next = [...parts];
    next[next.length - 1] = SEN_JSON;
    return next.join('/');
  }

  return null;
}

/**
 * Resolve target locale file path from an English source path.
 * @param {string} sourcePath e.g. game/en/en.json or game/en.json
 * @param {string} targetLocale e.g. bg
 * @returns {string|null}
 */
export function targetPathForLocale(sourcePath, targetLocale) {
  if (!sourcePath || !targetLocale || isEnglishLocale(targetLocale)) return null;
  if (!isTranslatableLocale(targetLocale)) return null;

  const targetFile = localeFileName(targetLocale);
  if (!targetFile) return null;

  const targetFolder = targetFile.replace(/\.json$/i, '');
  const parts = sourcePath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const sourceLocale = localeFromPath(sourcePath);
  if (!sourceLocale || !isEnglishLocale(sourceLocale)) return null;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  const parent = parts.length >= 2 ? parts[parts.length - 2] : null;

  if (
    parent &&
    normalizeLocaleCode(parent) === sourceLocale &&
    normalizeLocaleCode(fileBase) === sourceLocale
  ) {
    const next = [...parts];
    next[next.length - 2] = targetFolder;
    next[next.length - 1] = targetFile;
    return next.join('/');
  }

  if (normalizeLocaleCode(fileBase) === sourceLocale) {
    const next = [...parts];
    next[next.length - 1] = targetFile;
    return next.join('/');
  }

  return null;
}

/**
 * List path for discovering sibling locale files (e.g. game/en/en.json → game/).
 * @param {string} sourcePath
 * @returns {string|null}
 */
export function discoveryListPath(sourcePath) {
  const sourceLocale = localeFromPath(sourcePath);
  if (!sourcePath || !sourceLocale || !isEnglishLocale(sourceLocale)) return null;

  const parts = sourcePath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  const parent = parts.length >= 2 ? parts[parts.length - 2] : null;

  let prefixParts;
  if (
    parent &&
    normalizeLocaleCode(parent) === sourceLocale &&
    normalizeLocaleCode(fileBase) === sourceLocale
  ) {
    prefixParts = parts.slice(0, -2);
  } else if (normalizeLocaleCode(fileBase) === sourceLocale) {
    prefixParts = parts.slice(0, -1);
  } else {
    return null;
  }

  return prefixParts.length ? `${prefixParts.join('/')}/` : '/';
}

/**
 * @param {string} sourcePath
 * @returns {boolean}
 */
export function isNestedLocaleLayout(sourcePath) {
  const sourceLocale = localeFromPath(sourcePath);
  if (!sourceLocale) return false;

  const parts = sourcePath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts.length < 2) return false;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  const parent = parts[parts.length - 2];
  return (
    normalizeLocaleCode(parent) === sourceLocale &&
    normalizeLocaleCode(fileBase) === sourceLocale
  );
}

/**
 * Locale subfolders to list when targets live under per-locale directories.
 * @param {string} sourcePath
 * @param {Array<{ type: string, path: string, basename: string }>} listEntries
 * @returns {string[]}
 */
export function nestedLocaleDirsToProbe(sourcePath, listEntries) {
  if (!isNestedLocaleLayout(sourcePath) || !Array.isArray(listEntries)) return [];

  const localeDirs = new Set(
    COMMON_TARGET_LOCALES.map(item => normalizeLocaleCode(item.value))
  );

  return listEntries
    .filter(
      item =>
        item?.type === 'dir' &&
        localeDirs.has(normalizeLocaleCode(item.basename))
    )
    .map(item => item.path);
}

/**
 * Locales whose target JSON path already exists in bucket listings.
 * @param {string} sourcePath
 * @param {Array<{ type: string, path: string }>} listEntries
 * @returns {string[]}
 */
export function existingTargetLocalesFromListings(sourcePath, listEntries) {
  if (!sourcePath || !Array.isArray(listEntries)) return [];

  const filePaths = new Set(
    listEntries
      .filter(item => item?.type === 'file' && item.path)
      .map(item => normalizeStoragePath(item.path))
  );

  const normalizedSource = normalizeStoragePath(sourcePath);
  const found = [];

  const senPath = senPathFromEnglishPath(normalizedSource);
  if (senPath && filePaths.has(normalizeStoragePath(senPath))) {
    found.push(SEN_JSON);
  }

  for (const { value: locale } of COMMON_TARGET_LOCALES) {
    const targetPath = targetPathForLocale(normalizedSource, locale);
    if (targetPath && filePaths.has(normalizeStoragePath(targetPath))) {
      found.push(locale);
    }
  }
  return found;
}

const ENGLISH_REFERENCE_FILE = 'en.json';
const ENGLISH_REFERENCE_FOLDER = 'en';

/**
 * Resolve en.json from sen.json (game/sen/sen.json → game/en/en.json).
 * @param {string} senPath
 * @returns {string|null}
 */
export function englishPathFromSenPath(senPath) {
  if (!isSenJsonPath(senPath)) return null;

  const parts = senPath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const parent = parts.length >= 2 ? parts[parts.length - 2] : null;

  if (parent && normalizeLocaleCode(parent) === SEN_FOLDER) {
    const next = [...parts];
    next[next.length - 2] = ENGLISH_REFERENCE_FOLDER;
    next[next.length - 1] = ENGLISH_REFERENCE_FILE;
    return next.join('/');
  }

  const next = [...parts];
  next[next.length - 1] = ENGLISH_REFERENCE_FILE;
  return next.join('/');
}

/**
 * Resolve companion English file path from a locale path.
 * @param {string} localePath
 * @returns {string|null}
 */
export function englishPathFromLocalePath(localePath) {
  if (!localePath) return null;
  if (isSenJsonPath(localePath)) {
    return englishPathFromSenPath(localePath);
  }

  const sourceLocale = localeFromPath(localePath);
  if (!sourceLocale || isEnglishLocale(sourceLocale)) return null;

  const parts = localePath.replace(/\/+$/, '').split('/').filter(Boolean);
  if (!parts.length) return null;

  const fileBase = parts[parts.length - 1].replace(/\.json$/i, '');
  const parent = parts.length >= 2 ? parts[parts.length - 2] : null;

  if (
    parent &&
    normalizeLocaleCode(parent) === sourceLocale &&
    normalizeLocaleCode(fileBase) === sourceLocale
  ) {
    const next = [...parts];
    next[next.length - 2] = ENGLISH_REFERENCE_FOLDER;
    next[next.length - 1] = ENGLISH_REFERENCE_FILE;
    return next.join('/');
  }

  if (normalizeLocaleCode(fileBase) === sourceLocale) {
    const next = [...parts];
    next[next.length - 1] = ENGLISH_REFERENCE_FILE;
    return next.join('/');
  }

  return null;
}

const LOCALE_DIR_NAME_RE = /^[a-z]{2}([-_][a-z0-9]{2,6})?$/i;

/**
 * Locale JSON files visible from a folder listing (flat files and locale subfolders).
 * @param {Array<{ type: string, path: string, basename: string }>} items
 * @returns {{ locale: string, path: string }[]}
 */
export function discoverLocaleFilesInFolder(items) {
  if (!Array.isArray(items)) return [];

  const byLocale = new Map();

  for (const item of items) {
    if (!item?.path) continue;

    if (item.type === 'file' && /\.json$/i.test(item.path)) {
      if (isSenJsonPath(item.path)) {
        byLocale.set(SEN_JSON, item.path.replace(/\/+$/, ''));
        continue;
      }

      const locale = localeFromPath(item.path);
      if (!locale) continue;
      byLocale.set(normalizeLocaleCode(locale), item.path.replace(/\/+$/, ''));
      continue;
    }

    if (item.type === 'dir' && item.basename) {
      const dirLocale = normalizeLocaleCode(item.basename);
      if (
        !dirLocale ||
        GENERIC_JSON_NAMES.has(dirLocale) ||
        !LOCALE_DIR_NAME_RE.test(dirLocale)
      ) {
        continue;
      }

      const dirPath = item.path.replace(/\/+$/, '');
      const nestedPath = `${dirPath}/${item.basename}.json`;
      if (!byLocale.has(dirLocale)) {
        byLocale.set(dirLocale, nestedPath);
      }
    }
  }

  return [...byLocale.entries()].map(([locale, path]) => ({ locale, path }));
}

/**
 * Whether a folder listing is suitable for Accuracy Audit (en.json + other locales).
 * @param {Array<{ type: string, path: string, basename: string }>} items
 * @returns {{ eligible: boolean, englishPath: string|null, targets: { locale: string, path: string }[] }}
 */
export function folderAccuracyAuditPlan(items) {
  const files = discoverLocaleFilesInFolder(items);
  const english = files.find(entry => isEnglishLocale(entry.locale));
  const targets = files.filter(
    entry => !isEnglishLocale(entry.locale) && isTranslatableLocale(entry.locale)
  );

  return {
    eligible: Boolean(english) && targets.length > 0,
    englishPath: english?.path || null,
    targets
  };
}
